import { rejectComponentId } from '../schemas/component-id';

import type { CreateComponentInput } from './components';

/**
 * What the create form holds, and the body it becomes.
 *
 * ## Why this is a module and not fifteen lines inside the dialog
 *
 * `apps/web`'s vitest runs on `environment: 'node'`, so nothing rendered is
 * testable here — a payload built inside `submit()` is a payload no test can
 * see. This one decides what a permanent row says and which flags get bundled
 * into a thing a museum will be billed for, which is not a decision to leave
 * where nothing can look at it. `feature-flags/api/definition-create.ts` is the
 * same arrangement for the same reason.
 */

/** Every field as typed, untrimmed. `members` is the raw textarea. */
export interface ComponentDraft {
    readonly id: string;
    readonly displayName: string;
    readonly description: string;
    readonly owner: string;
    /** One flag key per line — see {@link parseMemberList}. */
    readonly members: string;
    readonly ownerService: string;
    readonly activationPath: string;
    readonly reason: string;
}

export const EMPTY_COMPONENT_DRAFT: ComponentDraft = {
    id: '',
    displayName: '',
    description: '',
    owner: '',
    members: '',
    ownerService: '',
    activationPath: '',
    reason: '',
};

/**
 * Read the members textarea into flag keys.
 *
 * A textarea rather than a picker, deliberately, for this slice: the flags list
 * is a separate query, an entitlement flag may not exist yet when its bundle is
 * assembled, and a bundle is in practice pasted in from a ticket or a
 * spreadsheet column. Commas are accepted alongside newlines so a pasted
 * comma-separated list does not become one very long key.
 *
 * Blank lines are dropped — an empty key is never something the operator meant.
 * Duplicates are **not**: the API refuses the same flag twice in one bundle on
 * purpose, and collapsing them here would make the request disagree with what
 * was typed and hide the mistake rather than report it.
 */
export function parseMemberList(members: string): string[] {
    return members
        .split(/[\n,]/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

/**
 * Build a `POST /components` body from the create form, or `null` while the
 * draft cannot be sent.
 *
 * `null` is the one state with no body: an id that the API's own rule would
 * refuse, or a required field still blank. The id rule is mirrored client-side
 * (`schemas/component-id.ts`) because an id is permanent, so it is worth
 * refusing before the round trip rather than after.
 *
 * An empty member list is explicitly *not* one of those states. A component
 * with no flags yet is legitimate — the API accepts it, and the whole point of
 * creation granting nothing is that a bundle can be assembled early.
 */
export function componentCreateInputFrom(draft: ComponentDraft): CreateComponentInput | null {
    const id = draft.id.trim();
    const displayName = draft.displayName.trim();
    const description = draft.description.trim();
    const owner = draft.owner.trim();

    if (rejectComponentId(id) !== null) return null;
    if (displayName.length === 0 || description.length === 0 || owner.length === 0) return null;

    const ownerService = draft.ownerService.trim();
    const activationPath = draft.activationPath.trim();
    const reason = draft.reason.trim();

    return {
        id,
        displayName,
        description,
        owner,
        flagKeys: parseMemberList(draft.members),
        // Omitted rather than sent blank. `ownerService` is `.url()` server
        // side, so an empty string is a 400 on a field the operator left alone.
        ...(ownerService.length > 0 ? { ownerService } : {}),
        ...(activationPath.length > 0 ? { activationPath } : {}),
        ...(reason.length > 0 ? { reason } : {}),
    };
}
